// Zustand und Versprechen der Rückfrage.
//
// ⚠️ Das hier ist der eigentlich schwierige Teil des Bausteins, und er ist die Bedingung dafür,
// dass die siebzehn Umstellungen kleine Änderungen bleiben.
//
// `window.confirm()` ist SYNCHRON: es hält den Hauptfaden an und gibt einen `boolean` zurück. Alle
// siebzehn gemessenen Aufrufe sehen deshalb so aus —
//
//     if (!window.confirm('…')) return;
//
// — eine Zeile, mitten in einer Funktion, die sonst nichts von Dialogen weiß. Ein React-Dialog ist
// naturgemäß asynchron: er wird gerendert, irgendwann klickt jemand, und erst dann steht die
// Antwort fest. Der geradlinige Nachbau wäre, jede der siebzehn Funktionen in zwei Hälften zu
// zerlegen (eine, die den Dialog öffnet, eine, die als Rückruf am Knopf hängt) und den
// Zwischenzustand — welche Zeile war noch mal gemeint? — in einem zusätzlichen `useState` zu
// parken. Das wären siebzehn Umbauten statt siebzehn Zeilen, und in jedem dieser Umbauten steckte
// ein neuer möglicher Fehler.
//
// Deshalb gibt `frage` ein VERSPRECHEN zurück. Aus der Aufrufstelle wird:
//
//     if (!(await frage({ titel: '…', text: '…', gefahr: true }))) return;
//
// Dieselbe Form, dieselbe Stelle, ein `await` mehr. Die Funktion muss dafür `async` sein — bei den
// Löschpfaden ist sie das ohnehin, weil dahinter ein Netzwerk-Aufruf steht.
//
// ── Die beiden Fallen, die ein Versprechen mitbringt ────────────────────────────────────────────
//
// **1. Ein Versprechen, das nie eingelöst wird.** Wer eine Zusage vergibt und den zugehörigen
// `resolve` verliert, hält den Aufrufer für immer an. Das ist schlimmer als ein Fehler: es passiert
// still. Konkret droht es, wenn die Seite abgebaut wird, während die Frage offen steht — die
// Nutzerin klickt „Löschen", wechselt über das Menü auf eine andere Seite, der Dialog verschwindet
// mitsamt der Seite, und der `await` im Löschpfad kehrt nie zurück. Was dahinter steht, läuft nie:
// kein `finally`, also bleibt etwa ein `setBusyId(null)` aus, und bei einer Seite, die währenddessen
// zurückkommt, hinge die Zeile dauerhaft im Ladezustand.
//
// Gelöst durch den Aufräum-Rückgabewert des Effekts unten: beim Abbau wird eine noch offene Frage
// mit `false` eingelöst. `false` und nicht `true` ist keine Geschmacksfrage — die unbeantwortete
// Frage ist eine NICHT erteilte Zustimmung, und die Aktionen dahinter löschen.
//
// **2. Zwei Fragen, die sich überlagern.** Stellt der Aufrufer eine zweite Frage, während die erste
// noch offen ist, verlöre eine naive Fassung den ersten `resolve` beim Überschreiben — Falle 1,
// nur ohne Abbau. Deshalb wird die ältere Frage hier zuerst ausdrücklich mit `false` eingelöst und
// dann ersetzt. Auch das ist die ehrliche Antwort: ihr Dialog ist vom Bildschirm verschwunden, ohne
// dass jemand ihn bestätigt hat.
//
// ── Warum der Zustand `rückfrage` heißt und nicht `inhalt` ──────────────────────────────────────
// Jede der zwölf umzustellenden Dateien hat bereits `const { inhalt, melde, schließe } =
// useBenachrichtigung()` stehen. Hieße das Feld hier ebenfalls `inhalt`, brächte jede einzelne
// Umstellung eine Namenskollision mit und müsste umbenennen — genau die Art Reibung, die dazu
// führt, dass jemand den Baustein am Ende doch nicht nimmt.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { RückfrageWunsch } from '../components/ui/Rueckfrage';

export interface RückfrageSteuerung {
  /** Die offene Frage, oder `null`. Direkt an `<Rueckfrage rückfrage={…} />` weiterreichen. */
  rückfrage: RückfrageWunsch | null;
  /** Stellt die Frage und löst mit der Antwort auf. `false` heißt: nicht bestätigt. */
  frage: (wunsch: RückfrageWunsch) => Promise<boolean>;
  /** Nimmt die Antwort entgegen. Hängt an `<Rueckfrage onAntwort={…} />`. */
  antworte: (bestätigt: boolean) => void;
}

export const useRueckfrage = (): RückfrageSteuerung => {
  const [rückfrage, setRückfrage] = useState<RückfrageWunsch | null>(null);

  // Der `resolve` der offenen Frage. Bewusst eine Referenz und kein Zustand: er darf beim Setzen
  // kein Rendern auslösen, und er muss im Aufräum-Rückgabewert unten noch lesbar sein.
  const offeneAntwortRef = useRef<((bestätigt: boolean) => void) | null>(null);

  // Löst genau einmal ein. Das Nullen VOR dem Aufruf ist der Schutz gegen den Doppelklick auf
  // „Löschen": der zweite Aufruf findet nichts mehr vor und läuft ins Leere, statt ein bereits
  // eingelöstes Versprechen erneut anzufassen.
  const löse = useCallback((bestätigt: boolean) => {
    const antwortGeben = offeneAntwortRef.current;
    offeneAntwortRef.current = null;
    antwortGeben?.(bestätigt);
  }, []);

  const frage = useCallback(
    (wunsch: RückfrageWunsch) => {
      // Falle 2: eine noch offene Frage zuerst verneinen, sonst ginge ihr `resolve` verloren.
      löse(false);
      // Das Versprechen wird VOR `setRückfrage` gebaut. Der Ausführer eines `Promise` läuft
      // synchron, die Referenz steht damit, bevor überhaupt gerendert werden kann — es gibt keinen
      // Zwischenzustand, in dem ein Dialog sichtbar wäre, dessen Antwort niemand entgegennimmt.
      const versprechen = new Promise<boolean>((auflösen) => {
        offeneAntwortRef.current = auflösen;
      });
      setRückfrage(wunsch);
      return versprechen;
    },
    [löse],
  );

  const antworte = useCallback(
    (bestätigt: boolean) => {
      setRückfrage(null);
      löse(bestätigt);
    },
    [löse],
  );

  // Falle 1: Abbau mit offener Frage. `löse` ist über `useCallback([])` unveränderlich, der Effekt
  // läuft deshalb genau einmal und sein Aufräumen genau beim Abbau — nicht bei jeder Frage.
  useEffect(() => () => löse(false), [löse]);

  return { rückfrage, frage, antworte };
};
